import { useState } from 'react';
import { StatusBadge } from '../../shared/components/common/Badges';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import { formatCurrency, formatCurrencyDynamic } from '../../utils/numberUtils';
import { toBoolean } from '../../utils/booleanUtils';
import LocationComplaintTatCard from './LocationComplaintTatCard';

const formatDateTime = (dateString) => {
  if (!dateString) return { date: '—', time: '' };
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return { date: '—', time: '' };
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  };
};

function StackedDateTime({ dateString }) {
  const { date, time } = formatDateTime(dateString);
  if (date === '—') return <span>—</span>;
  return (
    <span className="location-view-details__datetime" title={new Date(dateString).toLocaleString()}>
      <span>{date}</span>
      {time && <span className="location-view-details__datetime-time">{time}</span>}
    </span>
  );
}

function CurrencyValue({ amount }) {
  if (!amount || amount <= 0) {
    return <span className="location-view-details__muted">Not set</span>;
  }
  return (
    <span>
      {formatCurrency(amount)}
      <span className="location-view-details__currency-compact">
        ({formatCurrencyDynamic(amount)})
      </span>
    </span>
  );
}

function LocationFactsBlock({ title, facts }) {
  if (!facts.length) return null;
  return (
    <section className="location-view-details__block">
      <h4 className="location-view-details__block-title">{title}</h4>
      <div className="location-view-details__facts">
        {facts.map((fact) => (
          <div
            key={fact.key}
            className={`location-view-details__fact${fact.wide ? ' location-view-details__fact--wide' : ''}`}
          >
            <span className="location-view-details__fact-label">{fact.label}</span>
            <span className="location-view-details__fact-value">{fact.value}</span>
            {fact.hint && (
              <span className="location-view-details__fact-hint">{fact.hint}</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function LocationViewDetails({ location, currentUser, tatState }) {
  const [isCopied, setIsCopied] = useState(false);

  if (!location) return null;

  const handleCopyLocationCode = async () => {
    try {
      await navigator.clipboard.writeText(location.location_code);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy location code:', error);
    }
  };

  const addressLine = [
    location.address,
    location.city,
    location.state,
    location.country,
  ].filter(Boolean).join(', ');

  const basicFacts = [
    {
      key: 'email',
      label: 'Contact email',
      value: location.contact_email || '—',
    },
    {
      key: 'phone',
      label: 'Contact phone',
      value: location.contact_phone ? formatPhoneNumber(location.contact_phone) : '—',
    },
    ...(addressLine ? [{
      key: 'address',
      label: 'Address',
      value: addressLine,
      wide: true,
    }] : []),
  ];

  const capacityFacts = [
    {
      key: 'status',
      label: 'Status',
      value: (
        <StatusBadge
          status={toBoolean(location.is_active) ? 'active' : 'inactive'}
          size="small"
        />
      ),
    },
    {
      key: 'max-users',
      label: 'Max users',
      value: location.max_users ?? '—',
    },
    {
      key: 'max-devices',
      label: 'Max devices',
      value: location.max_devices ?? '—',
    },
  ];

  const financialFacts = [
    {
      key: 'budget',
      label: 'Annual budget',
      value: <CurrencyValue amount={location.annual_budget} />,
      hint: currentUser?.role === 'superadmin'
        ? 'Superadmin only — edit location to change budget.'
        : 'Configured by superadmin.',
    },
  ];

  const approvalFacts = [
    {
      key: 'threshold',
      label: 'High-cost approval threshold',
      value: <CurrencyValue amount={location.high_cost_approval_threshold} />,
      hint: currentUser?.role === 'admin'
        ? 'Devices pending approval above this cost are flagged as high-cost.'
        : 'Configured by the location administrator.',
    },
  ];

  const historyFacts = [
    {
      key: 'created',
      label: 'Created',
      value: <StackedDateTime dateString={location.created_at} />,
    },
    {
      key: 'updated',
      label: 'Last updated',
      value: <StackedDateTime dateString={location.updated_at} />,
    },
  ];

  return (
    <div className="location-view-details">
      <div className="location-view-details__section location-view-details__section--identity">
        <div className="location-view-details__identity-main">
          <span className="location-view-details__label">Location</span>
          <h3 className="location-view-details__name">{location.name}</h3>
        </div>
        <div className="location-view-details__identity-code">
          <span className="location-view-details__label">Location code</span>
          <button
            type="button"
            className={`location-view-details__code-badge ${isCopied ? 'is-copied' : ''}`}
            onClick={handleCopyLocationCode}
            title="Click to copy location code"
          >
            <span>{location.location_code}</span>
            <span aria-hidden="true">{isCopied ? '✓' : '📋'}</span>
          </button>
        </div>
      </div>

      <LocationFactsBlock title="Basic information" facts={basicFacts} />

      <LocationFactsBlock title="Capacity & status" facts={capacityFacts} />
      <LocationFactsBlock title="Financial information" facts={financialFacts} />
      <LocationFactsBlock title="Approval settings" facts={approvalFacts} />

      <section className="location-view-details__block">
        <h4 className="location-view-details__block-title">Complaint TAT policy</h4>
        <LocationComplaintTatCard tatState={tatState} />
      </section>

      <LocationFactsBlock title="Record history" facts={historyFacts} />
    </div>
  );
}

export default LocationViewDetails;
